// services/hobblivision/imageProcessor.js

/**
 * Loads and prepares an image for HobbliVision processing.
 *
 * Responsibilities:
 * - Validate image
 * - Load image into memory
 * - Extract metadata
 * - Create processing canvas
 * - Provide utilities for future preprocessing
 */
export async function processImage(file) {
    validateImage(file);
    const imageUrl = URL.createObjectURL(file);
    const image = await loadImage(imageUrl);
    const width = image.naturalWidth;
    const height = image.naturalHeight;
    const canvas = createCanvas(
        image,
        width,
        height
    );

    return {
        // Original upload
        original: file,
        // Loaded browser image
        processed: image,
        // Object URL
        imageUrl,
        // Dimensions
        width,
        height,
        // Orientation
        orientation:
            width >= height
                ? "landscape"
                : "portrait",
        // Canvas representation
        // used by future processing steps
        canvas,
        context:
            canvas.getContext("2d"),

        // Future transformations
        rotation: 0,

        // File metadata
        mimeType: file.type,
        fileSize: file.size,
        fileName: file.name,
        createdAt:
            new Date().toISOString()
    };
}

/**
 * Validate uploaded file.
 */
function validateImage(file) {
    if (!file) {
        throw new Error(
            "No image file provided."
        );
    }

    if (!file.type.startsWith("image/")) {
        throw new Error(
            "Selected file is not an image."
        );
    }
}

/**
 * Loads image into memory.
 */
function loadImage(src) {
    return new Promise(
        (resolve, reject) => {
            const img = new Image();
            img.onload = () =>
                resolve(img);
            img.onerror = () =>
                reject(
                    new Error(
                        "Unable to load image."
                    )
                );
            img.src = src;
        }
    );
}

/**
 * Creates a canvas copy.
 *
 * Future processing will happen here:
 *
 * - crop
 * - rotate
 * - grayscale
 * - contrast
 * - sharpening
 */
function createCanvas(
    image,
    width,
    height
) {
    const canvas =
        document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx =
        canvas.getContext("2d");
    ctx.drawImage(
        image,
        0,
        0,
        width,
        height
    );

    return canvas;
}



/**
 * Convert canvas to grayscale.
 *
 * Used later for OCR.
 */
export function grayscaleCanvas(
    canvas
) {
    const ctx =
        canvas.getContext("2d");
    const imageData =
        ctx.getImageData(
            0,
            0,
            canvas.width,
            canvas.height
        );
    const pixels =
        imageData.data;
    for (
        let i = 0;
        i < pixels.length;
        i += 4
    ) {
        const average =
            (
                pixels[i] +
                pixels[i + 1] +
                pixels[i + 2]
            ) / 3;
        pixels[i] =
            average;
        pixels[i + 1] =
            average;
        pixels[i + 2] =
            average;
    }
    ctx.putImageData(
        imageData,
        0,
        0
    );
    return canvas;
}

/**
 * Resize canvas while maintaining aspect ratio.
 *
 * Useful before OCR.
 */
export function resizeCanvas(
    canvas,
    maxWidth = 1200
) {
    if (
        canvas.width <= maxWidth
    ) {
        return canvas;
    }

    const scale =
        maxWidth /
        canvas.width;
    const resized =
        document.createElement(
            "canvas"
        );
    resized.width =
        maxWidth;
    resized.height =
        canvas.height * scale;
    const ctx =
        resized.getContext("2d");
    ctx.drawImage(
        canvas,
        0,
        0,
        resized.width,
        resized.height
    );
    return resized;
}

/**
 * Extract image regions based on detector layout.
 *
 * Regions use normalized coordinates:
 * x/y/width/height = 0-1
 */
export function extractRegions(imageData, layout) {
    if (!layout?.regions) {
        return {};
    }

    const regions = {};

    Object.entries(layout.regions).forEach(([name, region]) => {
        regions[name] = cropRegion(imageData, region);
    });

    return regions;
}


/**
 * Crop a normalized image region.
 */
function cropRegion(imageData, region) {
    const { canvas } = imageData;
    const cropCanvas = document.createElement("canvas");
    const ctx = cropCanvas.getContext("2d");
    const x = canvas.width * region.x;
    const y = canvas.height * region.y;
    const width = canvas.width * region.width;
    const height = canvas.height * region.height;

    cropCanvas.width = width;
    cropCanvas.height = height;

    ctx.drawImage(
        canvas,
        x,
        y,
        width,
        height,
        0,
        0,
        width,
        height
    );
    return cropCanvas;
}

/**
 * Extract cropped collectible candidates from detector output.
 * Current Version:
 * - Supports the detector's single bounding box.
 * Future:
 * - Multiple detected objects.
 */
export function extractCandidates(imageData, detection) {
    if (
        !detection?.boundary?.boundingBox
    ) {
        return [];
    }

    const box =
        detection.boundary.boundingBox;

    const cropCanvas =
        document.createElement("canvas");
        cropCanvas.width =
            Math.round(box.width);
        cropCanvas.height =
            Math.round(box.height);

    const ctx =
        cropCanvas.getContext("2d");

    ctx.drawImage(
        imageData.canvas,
        box.x,
        box.y,
        box.width,
        box.height,
        0,
        0,
        box.width,
        box.height
    );

    return [
        {
            id: 1,
            confidence:
                detection.boundary.confidence,
            bounds: box,
            canvas: cropCanvas,
            imageUrl:
                cropCanvas.toDataURL("image/png")

        }
    ];
}

/**
 * ============================================================
 * EXTRACT REGIONS FROM DETECTED ROI
 * ============================================================
 *
 * Takes the detector's cropped collectible image and applies
 * the normalized region layout to THAT image.
 *
 * This is intentionally separate from the existing
 * extractRegions() function so we don't disturb the current
 * image-processing pipeline.
 *
 * Example:
 * detector crop
 *      ↓
 *   ┌───────────────┐
 *   │     LABEL     │
 *   ├───────────────┤
 *   │               │
 *   │      CARD     │
 *   │               │
 *   ├───────────────┤
 *   │ CERTIFICATION │
 *   └───────────────┘
 * becomes:
 * {
 *   label: canvas,
 *   card: canvas,
 *   certification: canvas
 * }
 */
export function extractRegionsFromROI(
    roiImage,
    layout
) {
    if (!roiImage) {
        console.warn(
            "HobbliVision: No ROI image supplied for region extraction."
        );
        return {};
    }

    if (!layout?.regions) {
        console.warn(
            "HobbliVision: No region layout supplied for ROI extraction."
        );
        return {};
    }

    /*
     * The detector's croppedImage should normally be
     * an HTMLCanvasElement.
     * We also tolerate an image-like object with width
     * and height so this remains useful as the pipeline
     * evolves.
     */
    const sourceWidth =
        roiImage.width;

    const sourceHeight =
        roiImage.height;

    if (
        !sourceWidth ||
        !sourceHeight
    ) {
        console.warn(
            "HobbliVision: ROI has invalid dimensions."
        );

        return {};
    }

    const regions = {};

    Object.entries(
        layout.regions
    ).forEach(
        ([name, region]) => {
            if (!region) {
                return;
            }
            const x =
                Math.max(
                    0,
                    Math.round(
                        region.x *
                            sourceWidth
                    )
                );

            const y =
                Math.max(
                    0,
                    Math.round(
                        region.y *
                            sourceHeight
                    )
                );

            const width =
                Math.min(
                    sourceWidth - x,
                    Math.round(
                        region.width *
                            sourceWidth
                    )
                );

            const height =
                Math.min(
                    sourceHeight - y,
                    Math.round(
                        region.height *
                            sourceHeight
                    )
                );

            if (
                width <= 0 ||
                height <= 0
            ) {
                console.warn(
                    `HobbliVision: Region "${name}" has invalid dimensions.`,
                    {
                        x,
                        y,
                        width,
                        height
                    }
                );
                return;
            }

            const canvas =
                document.createElement(
                    "canvas"
                );
            canvas.width = width;
            canvas.height = height;
            const context =
                canvas.getContext(
                    "2d"
                );
            if (!context) {
                return;
            }
            context.drawImage(
                roiImage,
                x,
                y,
                width,
                height,
                0,
                0,
                width,
                height
            );
            regions[name] = {
                name,
                canvas,
                x,
                y,
                width,
                height,
                normalized: {
                    x: region.x,
                    y: region.y,
                    width: region.width,
                    height: region.height
                }
            };
        }
    );
    return regions;
}

/**
 * ============================================================
 * DETECT LIKELY TEXT REGIONS INSIDE ROI
 * ============================================================
 *
 * Kept here as the image-pipeline entry point so callers can
 * continue to treat ROI analysis as part of image processing.
 * The actual detector lives in textRegionDetector.js.
 */
export function extractTextRegionsFromROI(roiImage, textDetection) {
    if (!roiImage || !textDetection?.regions) {
        return {};
    }

    const regions = {};

    textDetection.regions.forEach((candidate, index) => {
        const x = Math.max(0, Math.round(candidate.source.x));
        const y = Math.max(0, Math.round(candidate.source.y));
        const right = Math.min(
            roiImage.width,
            x + Math.round(candidate.source.width)
        );
        const bottom = Math.min(
            roiImage.height,
            y + Math.round(candidate.source.height)
        );

        const width = right - x;
        const height = bottom - y;
        if (width <= 0 || height <= 0) return;

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext("2d");
        if (!context) return;

        context.drawImage(
            roiImage,
            x,
            y,
            width,
            height,
            0,
            0,
            width,
            height
        );

        regions[candidate.id || `text-${index + 1}`] = {
            name: candidate.id || `text-${index + 1}`,
            canvas,
            x,
            y,
            width,
            height,
            orientation: candidate.orientation || 0,
            type: candidate.type || "text",
            confidence: candidate.confidence || 0,
            textness: candidate.textness || 0,
            normalized: candidate.normalized,
        };
    });

    return regions;
}
